/**
 * AccountSheet — the קוֹל account/settings surface. A calm bottom sheet (not a
 * separate page) opened from the Home header: who you are, language, the
 * utility links (family, plan), and Log out. Keeps the conversational frame
 * intact — settings are a quiet drawer, never the main stage.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { Icon, type IconName } from './Icon';

const LANGS: Array<{ code: 'he' | 'en' | 'ar'; label: string }> = [
  { code: 'he', label: 'עברית' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
];

export function AccountSheet({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const who = user?.phone || user?.email || '';

  async function onLogout() {
    setBusy(true);
    try {
      await logout(); // clears the auth store in .finally even on network error
    } finally {
      navigate('/login', { replace: true });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center font-kol"
      role="dialog"
      aria-modal="true"
      aria-label={t('kol.account.title', { defaultValue: 'החשבון שלי' })}
      dir={i18n.dir()}
    >
      <button
        type="button"
        aria-label={t('common.close', { defaultValue: 'סגירה' })}
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
      />
      <div className="kol-glass-sheet relative w-full max-w-md rounded-t-[30px] px-6 pt-5 pb-[max(20px,env(safe-area-inset-bottom))] shadow-kol-up animate-kol-rise">
        <div className="mx-auto mb-5 h-[5px] w-10 rounded-full bg-kol-line" aria-hidden />

        <div className="flex items-center gap-3.5 mb-6">
          <div className="kol-orb shrink-0" data-state="idle" style={{ width: 48, height: 48 }} aria-hidden />
          <div className="min-w-0">
            <div className="text-[18px] font-extrabold text-kol-ink leading-tight">
              {t('kol.account.title', { defaultValue: 'החשבון שלי' })}
            </div>
            {who && <div className="text-[14px] text-kol-ink-faint tabular-nums truncate">{who}</div>}
          </div>
        </div>

        {/* Language */}
        <div className="mb-5">
          <div className="text-[13px] font-semibold text-kol-ink-faint mb-2">
            {t('kol.account.language', { defaultValue: 'שפה' })}
          </div>
          <div className="flex gap-2">
            {LANGS.map((l) => {
              const on = i18n.language === l.code;
              return (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => i18n.changeLanguage(l.code)}
                  className={[
                    'flex-1 rounded-2xl py-2.5 text-[15px] font-medium border min-h-[48px]',
                    on
                      ? 'bg-kol-sage-soft border-kol-pine text-kol-pine'
                      : 'bg-kol-card-warm border-kol-line text-kol-ink',
                  ].join(' ')}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Utility links */}
        <div className="flex flex-col gap-2 mb-5">
          <SheetLink
            icon="users"
            label={t('kol.account.family', { defaultValue: 'משפחה והרשאות' })}
            onClick={() => navigate('/family')}
          />
          <SheetLink
            icon="star"
            label={t('kol.account.plan', { defaultValue: 'המנוי שלי' })}
            onClick={() => navigate('/billing')}
          />
        </div>

        <button
          type="button"
          onClick={onLogout}
          disabled={busy}
          className="w-full rounded-2xl border border-[#ecd2c5] bg-kol-clay-soft text-[#9a5a32] font-semibold py-3.5 text-[16px] disabled:opacity-50"
        >
          {busy ? t('common.loading', { defaultValue: 'רגע…' }) : t('kol.account.logout', { defaultValue: 'התנתקות' })}
        </button>
      </div>
    </div>
  );
}

function SheetLink({ icon, label, onClick }: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3.5 rounded-2xl bg-kol-card-warm border border-kol-line px-4 py-3.5 text-[16px] font-medium text-kol-ink min-h-[52px] text-start"
    >
      <span className="w-9 h-9 rounded-[11px] grid place-items-center bg-kol-sage-soft text-kol-pine flex-none">
        <Icon name={icon} size={19} />
      </span>
      <span className="flex-1">{label}</span>
      <Icon name="chevron" size={18} className="text-kol-ink-faint" />
    </button>
  );
}
